import { ALL_ROUTES, ROUTES, type RouteDef } from '@/app/routes'
import { isNewsletterIssue, type NewsletterIssue } from '@/content/newsletterIssue'

/**
 * The archive roster: every published newsletter issue, newest first.
 *
 * Read the folder, do not keep a list. `content/pages/*.ts` is already globbed
 * three times over (by `en.ts` for the copy, by `routes.ts` for the routes, by
 * `tr.ts` for the translations) and an issue page declares its own descriptor,
 * so adding an issue is adding one file — which is the whole point of the
 * 2026-08-28 split. A central array here would be a fourth append into a shared
 * region, and two editor runs writing two issues would collide on it.
 *
 * WHY THIS IS RESOLVED AGAINST THE ROUTE REGISTRY rather than carrying paths of
 * its own: an archive card that links to a page the router does not serve is a
 * 301-into-nothing, and the whole newsletter is built around not doing that —
 * the sender refuses to mail a link that 404s
 * (`supabase/functions/website-newsletter-send`), so the archive must hold
 * itself to the same standard. Here it is not a check that can be forgotten, it
 * is the only way a card gets built at all.
 */
export interface ArchiveEntry {
  issue: NewsletterIssue
  route: RouteDef
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const MODULES = import.meta.glob<Record<string, unknown>>('./pages/*.ts', { eager: true })

function collect(): ArchiveEntry[] {
  const published = new Map(ROUTES.map((r) => [r.id, r]))
  const known = new Set(ALL_ROUTES.map((r) => r.id))
  const entries: ArchiveEntry[] = []
  const seen = new Set<string>()

  // Sorted by module path so two issues with the same date resolve the same way
  // on every machine — the same reason `pagesFrom` sorts.
  for (const path of Object.keys(MODULES).sort()) {
    for (const value of Object.values(MODULES[path])) {
      if (!isNewsletterIssue(value)) continue

      // Loud, at module load, in the prerender. Every one of these is a typo in
      // a file somebody just wrote, and the failure modes are all quiet: a bad
      // date sorts to one end of the archive, a duplicate silently shadows, and
      // a page id nobody declared renders as a card with no title and no href.
      if (!ISO_DATE.test(value.date)) {
        throw new Error(`${path}: newsletter issue date must be YYYY-MM-DD, got "${value.date}"`)
      }
      if (seen.has(value.page)) {
        throw new Error(`${path}: two newsletter issues claim the page "${value.page}"`)
      }
      seen.add(value.page)

      const route = published.get(value.page)
      if (!route) {
        // A DRAFT issue is absent from the production archive and that is
        // correct — `published()` in `app/routes.ts` has already removed its
        // route, and a preview build keeps both. An id in NEITHER list is a
        // typo, and it gets said out loud.
        if (known.has(value.page)) continue
        throw new Error(
          `${path}: newsletter issue names page "${value.page}", which is not a route. `
            + `The descriptor's \`page\` is the route id of the issue's own page.`,
        )
      }
      entries.push({ issue: value, route })
    }
  }

  // Newest first, then by route id — a stable tiebreak, so an archive of two
  // issues dated the same day is not a diff in every build.
  return entries.sort(
    (a, b) => b.issue.date.localeCompare(a.issue.date) || a.route.id.localeCompare(b.route.id),
  )
}

export const NEWSLETTER_ARCHIVE: ArchiveEntry[] = collect()
